// src/components/profiles/Member/components/profile/profile-avatars/ProfileAvatars.tsx

"use client";

import React, { useMemo } from "react";
import { useRouter } from "next/navigation";
import { useSelector } from "react-redux";
import { CustomUserType } from "../../../../../../types/profiles/authTypes";
import { VisitorFriend } from "@/types/profiles/visitorProfileTypes";
import { getDefaultAvatar } from "../../../../../../utils/helpers/avatarFallback";

import { AvatarDisplay, Loader, Button } from "../../../../../common";
import SocialOrbit, { getBucket } from "../../../../../common/social-orbit/SocialOrbit";

import "./ProfileAvatars.css";

interface ProfileAvatarsProps {
  profileImageUrl: string;
  placeholderSrc: string;
  customUser: CustomUserType;
  labelColor: string;
  isVerifiedIdentity: boolean | undefined;
  isTownlitVerified: boolean | undefined;
  friends?: VisitorFriend[];             // ✅ friends from Member serializer (randomized)
  loading?: boolean;                     // optional: allow parent to control loading UI
  onOpenFriendsFullView?: () => void;    // optional: for >160 case
}

const ProfileAvatars: React.FC<ProfileAvatarsProps> = ({
  profileImageUrl,
  placeholderSrc,
  customUser,
  labelColor,
  isVerifiedIdentity,
  isTownlitVerified,
  friends,
  loading = false,
  onOpenFriendsFullView,
}) => {
  const router = useRouter();
  const data: VisitorFriend[] = friends ?? [];

  // Render a single friend avatar
  const renderFriend = (friend: VisitorFriend, size: "md" | "sm") => {

    const src = friend.avatar_cdn_url || null;
    const placeholderSrc = friend.avatar_url || friend.profile_image || null;

    const alt =
      friend.username ||
      `${friend.name || ""} ${friend.family || ""}`.trim() ||
      "User";
    const borderColor = (friend as any).label?.color; // keep parity with existing shape
    const verifiedIdentity = !!(friend as any).is_verified_identity;
    const townlitVerified = !!(friend as any).is_townlit_verified;

    const onClick = () => (friend as any).profile_url && router.push((friend as any).profile_url);

    return (
      <AvatarDisplay
        userId={friend.id}
        src={src}
        placeholderSrc={placeholderSrc}
        alt={alt}
        title={alt}
        shape="rounded"
        size={size}
        onClick={onClick}
        borderColor={borderColor}
        isVerifiedIdentity={verifiedIdentity}
        isTownlitVerified={townlitVerified}
        withBorder
        type="user"
      />
    );
  };

  // Decide center avatar size by bucket (0 → mega, 1 → xl, else → lg)
  const centerSize = useMemo<"lg" | "xl" | "mega">(() => {
    const bucket = getBucket(data.length);
    if (bucket === 0) return "mega";
    if (bucket === 1) return "xl";
    return "lg";
  }, [data.length]);

  // Center (owner) avatar
  const renderCenter = () => (
    <div className="profile-center-wrapper">
      <AvatarDisplay
        userId={customUser.id}
        src={profileImageUrl}
        placeholderSrc={placeholderSrc}
        alt={
          customUser.name
            ? `${customUser.name}'s profile`
            : `${customUser.username}'s profile`
        }
        title={customUser.username || "User"}
        shape="rounded"
        size={centerSize}
        withBorder
        borderColor={labelColor}
        type="user"
        isVerifiedIdentity={isVerifiedIdentity}
        isTownlitVerified={isTownlitVerified}
      />

    </div>
  );

  // Keep the bg mapping identical to previous version
  const bgByBucket = {
    0: "bg-quiet",
    1: "bg-soft-accent",
    2: "bg-soft-glory",
    3: "bg-soft-admin",
    4: "bg-soft-highlight",
    5: "bg-soft-highlight",
  } as const;

  return (
    <SocialOrbit
      friends={data}
      loading={loading}
      renderCenter={renderCenter}
      renderFriend={(f, size) => renderFriend(f as VisitorFriend, size)}
      LoaderSlot={() => <Loader size="sm" variant="dots" color="glory" />}
      onOpenAll={onOpenFriendsFullView}
      className="net-dots"
      options={{ bgByBucket, clusterCap: 80 }}
    />
  );
};

export default ProfileAvatars;
